/**
 * Estado de sesión de la interfaz.
 *
 * Al arrancar se intenta UNA vez canjear el refresco. Si hay sesión viva -- la
 * cookie del navegador o el refresco guardado en la app -- el usuario entra sin
 * hacer nada; si no, se queda como visitante y la aplicación funciona igual,
 * porque la enciclopedia y el meta son públicos.
 *
 * `cargando` existe para no parpadear: sin él, la cabecera pinta "Entrar"
 * durante el primer viaje a la API y luego lo cambia por el nombre, que se lee
 * como un fallo.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, clearSession, onSession, refreshSession, setAccessToken } from './api.js';

const AuthContext = createContext(null);

/** Convierte la respuesta de error de la API en una clave de traducción. */
const errorKey = (status, body) => {
  const code = body?.error ?? '';
  const known = {
    email_taken: 'errEmailTaken',
    invalid_email: 'errInvalidEmail',
    weak_password: 'errWeakPassword',
    bad_credentials: 'errBadCredentials',
    invalid_invite: 'errInvalidInvite',
    invalid_token: 'errInvalidToken',
    too_many_requests: 'errTooMany',
  };
  return known[code] ?? (status >= 500 ? 'errServer' : 'errGeneric');
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    // El módulo de red avisa cuando refresca por su cuenta (tras un 401), para
    // que la interfaz no se quede mostrando a alguien que ya no está.
    onSession(setUser);
    /*
     * Un fallo PASAJERO al arrancar (`undefined`: 5xx, red) no es «no hay
     * sesión»: se reintenta con espera creciente (2+4+8 s) antes de rendirse,
     * con la pokébola en pantalla. Solo el 401 (`null`) concluye de una vez.
     */
    let vivo = true;
    const arrancar = async () => {
      for (let intento = 0; ; intento++) {
        const r = await refreshSession();
        if (r !== undefined || intento >= 3 || !vivo) break;
        await new Promise((ok) => setTimeout(ok, 2000 * 2 ** intento));
      }
      if (vivo) setCargando(false);
    };
    arrancar();
    return () => { vivo = false; onSession(null); };
  }, []);

  /**
   * Mantener vivo el token mientras la sesión está abierta.
   *
   * EL BUG QUE ARREGLA. El token de acceso dura 15 minutos. Las rutas públicas
   * -- meta, partidas, cartas -- NO responden 401 con un token caducado: sirven
   * la versión de visitante (plan gratis) con un 200 tan normal. Como no hay
   * 401, el reintento automático no se dispara, y quien deja la pestaña abierta
   * un rato vuelve, cambia de vista y de golpe "no es Pro": la interfaz lo sigue
   * mostrando dentro, pero la API ya lo trata como visitante.
   *
   * Se refresca en dos momentos, y solo con sesión abierta -- refrescar de
   * visitante seria pedir por pedir:
   *   - al VOLVER a la pestaña (visibilitychange/focus): cubre el caso real, que
   *     es dejarla de lado y volver mucho despues.
   *   - cada 12 minutos como red de seguridad mientras se usa sin cambiar de
   *     pestaña, por debajo de los 15 de vida del token.
   */
  // Se depende del BOOLEANO, no del objeto: cada refresco crea un `user` nuevo,
  // y con `[user]` los listeners se reengancharían en cada uno. Con `[logueado]`
  // solo se montan al entrar y se sueltan al salir.
  const logueado = !!user;

  /*
   * La exclusión de analítica de la CUENTA se vuelca al dispositivo: el
   * guardián de index.html corre antes de saberse quién eres, así que lo que
   * lee es esta marca, dejada aquí en la visita anterior. Solo se toca con
   * sesión resuelta -- de visitante no se borra, porque el dispositivo sigue
   * siendo del dueño aunque cierre sesión. Mismo criterio que arriba: se
   * depende del primitivo, no del objeto `user`.
   */
  const sinAnalytics = user ? !!user.sinAnalytics : null;
  useEffect(() => {
    if (sinAnalytics === null) return;
    try {
      if (sinAnalytics) localStorage.setItem('pt_sin_analytics_cuenta', '1');
      else localStorage.removeItem('pt_sin_analytics_cuenta');
    } catch { /* sin localStorage no hay marca, y no pasa nada */ }
  }, [sinAnalytics]);
  useEffect(() => {
    if (!logueado) return;

    const alVolver = () => {
      if (document.visibilityState === 'visible') refreshSession();
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    const cada = setInterval(refreshSession, 12 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
      clearInterval(cada);
    };
  }, [logueado]);

  /** Envía y devuelve `{ok, error}`; deja el usuario puesto si salió bien. */
  const post = useCallback(async (path, body) => {
    const res = await api(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
    let data = null;
    try {
      data = await res.json();
    } catch {
      /* respuesta sin cuerpo */
    }
    if (!res.ok) return { ok: false, error: errorKey(res.status, data) };
    if (data?.accessToken) {
      setAccessToken(data.accessToken);
      setUser(data.user ?? null);
    }
    return { ok: true, data };
  }, []);

  const value = useMemo(
    () => ({
      user,
      cargando,
      entrar: (email, password) => post('/api/auth/login', { email, password }),
      registrar: (datos) => post('/api/auth/register', datos),
      recuperar: (email) => post('/api/auth/forgot', { email }),
      restablecer: (token, password) => post('/api/auth/reset', { token, password }),
      reenviarVerificacion: () => post('/api/auth/resend-verification'),
      async salir() {
        await post('/api/auth/logout');
        clearSession();
        setAccessToken(null);
        setUser(null);
      },
      /** Relee el usuario: el plan y la verificación cambian fuera de la sesión. */
      async refrescarUsuario() {
        const r = await api('/api/auth/me');
        if (r.ok) setUser((await r.json()).user);
      },
    }),
    [user, cargando, post]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}
